import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Link } from 'foundations-components/transitional/components/Link';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import { useState } from 'react';
import CoreSeatsExplainerModal from 'ui-addon-upgrades/_core/common/components/seatsModals/CoreSeatsExplainerModal';
import { planBuilderQuantityUpdated } from '../../actions/PlanBuilderActions';
import { CREATE_A_BUNDLE } from '../../constants/tracking/TrackingSources';
import { useCalculatedPrice, useListPrice } from '../../hooks/useBulkPurchaseOptions';
import { useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { planBuilderCommonTermForProductConfigurationsSelector, planBuilderHasCommonTermsSelector, planBuilderIsLoadingSelector, planBuilderProductConfigurationsSelector } from '../../selectors/PlanBuilderSelectors';
import { includedCoreSeatsQuantitySelector, productLevelForCoreSeatsSelector } from '../../selectors/ProductConfigsSelectors';
import { coreSeatSkuForProductLevelSelector, productForSkuIdSelector } from '../../selectors/ProductsSelectors';
import { trackAdditionalUnitsPicker, trackModalInteraction } from '../../utils/tracking/trackingEventUtils';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import { StyledTitleContainer } from './SharedComponents';
import { useShoppingCartPrice } from './ShoppingCart/shoppingCartHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CoreSeatsPickerForPlanBuilder = () => {
  const dispatch = useAppDispatch();
  const isPlanBuilderLoading = useAppSelector(planBuilderIsLoadingSelector);
  const hasCommonTerms = useAppSelector(planBuilderHasCommonTermsSelector);
  const productConfigurations = useAppSelector(planBuilderProductConfigurationsSelector);
  const commonTermForProductConfigurations = useAppSelector(planBuilderCommonTermForProductConfigurationsSelector);
  const [coreSeatsExplainerVisible, setCoreSeatsExplainerVisible] = useState(false);
  const productLevelForCoreSeats = useAppSelector(state => productLevelForCoreSeatsSelector(state, {
    productConfigurations
  }));
  const {
    skuId: skuIdCoreSeat
  } = useAppSelector(state => coreSeatSkuForProductLevelSelector(state, {
    productLevel: productLevelForCoreSeats
  }));
  const coreSeatConfig = productConfigurations.find(({
    skuId
  }) => skuId === skuIdCoreSeat);
  const includedCoreSeatsQuantity = useAppSelector(state => {
    return includedCoreSeatsQuantitySelector(state, {
      productConfigurations
    });
  });
  const additionalCoreSeats = (coreSeatConfig === null || coreSeatConfig === void 0 ? void 0 : coreSeatConfig.quantity) || 0;
  const {
    apiName
  } = useAppSelector(state => productForSkuIdSelector(state, {
    skuId: skuIdCoreSeat
  }));
  const handleCoreSeatsChange = ({
    target: {
      value
    }
  }) => {
    const newQuantityWithoutIncludedCoreSeats = parseInt(value, 10) - includedCoreSeatsQuantity;
    const oldQuantityWithCoreSeats = additionalCoreSeats + includedCoreSeatsQuantity;
    dispatch(planBuilderQuantityUpdated({
      quantity: newQuantityWithoutIncludedCoreSeats,
      skuId: skuIdCoreSeat
    }));
    trackAdditionalUnitsPicker({
      action: `stepper ${newQuantityWithoutIncludedCoreSeats + includedCoreSeatsQuantity > oldQuantityWithCoreSeats ? 'increase' : 'decrease'}`,
      trackingSource: CREATE_A_BUNDLE,
      productName: apiName,
      skuId: skuIdCoreSeat,
      ownedUnitQuantity: 0,
      displayedUnitQuantity: newQuantityWithoutIncludedCoreSeats + includedCoreSeatsQuantity
    });
  };
  const currencyCode = useDisplayedCurrencyCode();
  const {
    monthlyCalculatedPrice: additionalSeatsDiscountedPrice
  } = useCalculatedPrice({
    queryParamTerm: commonTermForProductConfigurations,
    apiName
  });
  const {
    monthlyListPrice: additionalSeatsListPrice
  } = useListPrice({
    queryParamTerm: commonTermForProductConfigurations,
    apiName
  });
  const shoppingCartPrice = useShoppingCartPrice({
    productConfiguration: coreSeatConfig || createProductConfig({
      skuId: skuIdCoreSeat,
      quantity: includedCoreSeatsQuantity
    })
  });
  const additionalSeatsPrice = hasCommonTerms ? additionalSeatsDiscountedPrice : additionalSeatsListPrice;

  // Show $0 if no additional core seats are selected
  const price = !coreSeatConfig ? 0 : shoppingCartPrice;
  const {
    monthlyCalculatedPrice: coreSeatProPrice
  } = useCalculatedPrice({
    queryParamTerm: commonTermForProductConfigurations,
    apiName: 'core-seats-professional'
  });
  const handleLinkClick = () => {
    setCoreSeatsExplainerVisible(true);
    trackModalInteraction({
      action: 'click to open',
      name: 'core-seats-explainer-modal',
      productLevel: productLevelForCoreSeats,
      trackingSource: CREATE_A_BUNDLE
    });
  };
  const TotalCoreSeatsText = () => /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `interstitial.coreSeats.add.${productLevelForCoreSeats}_jsx`,
    elements: {
      Link: ({
        children
      }) => /*#__PURE__*/_jsx(Link, {
        onClick: handleLinkClick,
        children: children
      })
    }
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [coreSeatsExplainerVisible && coreSeatProPrice && /*#__PURE__*/_jsx(CoreSeatsExplainerModal, {
      handleModalClose: () => setCoreSeatsExplainerVisible(false),
      currencyCode: currencyCode,
      coreSeatProPrice: coreSeatProPrice
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(StyledTitleContainer, {
          children: /*#__PURE__*/_jsx(Text, {
            typography: "heading-50",
            children: /*#__PURE__*/_jsx(TotalCoreSeatsText, {})
          })
        }), /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          marginBlockEnd: "400",
          children: additionalSeatsPrice !== undefined && /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "planBuilder.priceDisplay.perMonthPerSeat",
            options: {
              price: /*#__PURE__*/_jsx(PriceDisplay, {
                price: additionalSeatsPrice,
                currencyCode: currencyCode
              })
            }
          })
        })]
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "end",
        flexDirection: "column",
        maxInlineSize: "100px",
        children: [/*#__PURE__*/_jsx(StepperInput, {
          value: additionalCoreSeats + includedCoreSeatsQuantity,
          min: includedCoreSeatsQuantity,
          onChange: handleCoreSeatsChange,
          "data-selenium-id": "core-seats-card-stepper-input",
          disabledMinButtonTooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "interstitial.seatsPicker.disabledMinButtonTooltip"
          }),
          disabledMinButtonTooltipProps: {
            placement: 'bottom',
            children: /*#__PURE__*/_jsx(_Fragment, {})
          }
        }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          heights: [24],
          widths: [90],
          isLoading: isPlanBuilderLoading || price === undefined,
          children: /*#__PURE__*/_jsxs(Text, {
            as: "span",
            marginInlineStart: "200",
            children: [/*#__PURE__*/_jsx(PriceDisplay, {
              price: price,
              currencyCode: currencyCode
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "planBuilder.priceDisplay.perMonth"
            })]
          })
        })]
      })]
    })]
  });
};
export default CoreSeatsPickerForPlanBuilder;